import React from "react";
import {
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    MenuItem,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from "@mui/material";
import {Eye, Link as LinkIcon, Receipt, Search} from "lucide-react";
import {formatCpfCnpj} from "../../../shared/utils";
import {formatDateBR, formatMoney, getReceivableStatusPalette} from "../financeiroUtils";

function ReceivableDetailsDialog({open, receivable, history, companies, boletos, onClose}) {
    const company = companies.find((item) => item.id === receivable?.empresaId);
    const boleto = boletos.find((item) => item.contaReceberId === receivable?.id);
    const palette = getReceivableStatusPalette(receivable?.status);

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
            <DialogTitle>Detalhes da conta a receber</DialogTitle>
            <DialogContent dividers>
                {!receivable ? null : (
                    <Stack spacing={3}>
                        <Box sx={{p: 2, borderRadius: 2.5, bgcolor: palette.bg, border: `1px solid ${palette.border}`}}>
                            <Typography sx={{fontWeight: 700, color: palette.color}}>Status: {receivable.status}</Typography>
                            <Typography variant="body2" sx={{color: palette.color, opacity: 0.9}}>{receivable.descricao}</Typography>
                        </Box>

                        <Box sx={{display: 'grid', gridTemplateColumns: {xs: '1fr', md: '1fr 1fr'}, gap: 2}}>
                            <Paper variant="outlined" sx={{p: 2.5, borderRadius: 2.5}}>
                                <Typography sx={{fontWeight: 600, color: '#111827', mb: 2}}>Cliente</Typography>
                                <Stack spacing={1}>
                                    <Typography variant="body2" color="text.secondary">Empresa: <Typography component="span" sx={{color: '#111827'}}>{company?.nome_fantasia || company?.razao_social || company?.nome || '-'}</Typography></Typography>
                                    <Typography variant="body2" color="text.secondary">CNPJ: <Typography component="span" sx={{color: '#111827'}}>{formatCpfCnpj(company?.cnpj || '') || '-'}</Typography></Typography>
                                    <Typography variant="body2" color="text.secondary">Competência: <Typography component="span" sx={{color: '#111827'}}>{receivable.competencia || '-'}</Typography></Typography>
                                </Stack>
                            </Paper>

                            <Paper variant="outlined" sx={{p: 2.5, borderRadius: 2.5}}>
                                <Typography sx={{fontWeight: 600, color: '#111827', mb: 2}}>Valores</Typography>
                                <Stack spacing={1}>
                                    <Typography variant="body2" color="text.secondary">Valor total: <Typography component="span" sx={{color: '#111827'}}>{formatMoney(receivable.valorTotal)}</Typography></Typography>
                                    <Typography variant="body2" color="text.secondary">Valor pago: <Typography component="span" sx={{color: '#111827'}}>{formatMoney(receivable.valorPago)}</Typography></Typography>
                                    <Typography variant="body2" color="text.secondary">Saldo: <Typography component="span" sx={{color: '#111827'}}>{formatMoney((receivable.valorTotal || 0) - (receivable.valorPago || 0))}</Typography></Typography>
                                </Stack>
                            </Paper>
                        </Box>

                        <Paper variant="outlined" sx={{p: 2.5, borderRadius: 2.5}}>
                            <Typography sx={{fontWeight: 600, color: '#111827', mb: 2}}>Vínculos</Typography>
                            <Stack spacing={1}>
                                <Typography variant="body2" color="text.secondary">Documento: <Typography component="span" sx={{color: '#111827'}}>{receivable.numeroDocumento || '-'}</Typography></Typography>
                                <Typography variant="body2" color="text.secondary">Boleto: <Typography component="span" sx={{color: '#111827'}}>{boleto?.nossoNumero || 'Não emitido'}</Typography></Typography>
                                <Typography variant="body2" color="text.secondary">Pagamento: <Typography component="span" sx={{color: '#111827'}}>{receivable.dataPagamento ? formatDateBR(receivable.dataPagamento) : '-'}</Typography></Typography>
                            </Stack>
                        </Paper>

                        <Paper variant="outlined" sx={{p: 2.5, borderRadius: 2.5}}>
                            <Typography sx={{fontWeight: 600, color: '#111827', mb: 2}}>Histórico</Typography>
                            {!history.length ? (
                                <Typography color="text.secondary">Nenhuma movimentação registrada.</Typography>
                            ) : (
                                <Stack spacing={1.25}>
                                    {history.map((item) => (
                                        <Box key={item.id} sx={{display: 'flex', justifyContent: 'space-between', gap: 2, pb: 1.25, borderBottom: '1px solid #e5e7eb'}}>
                                            <Box>
                                                <Typography sx={{color: '#111827', fontWeight: 500}}>{item.evento}</Typography>
                                                <Typography variant="body2" color="text.secondary">{item.descricao}</Typography>
                                            </Box>
                                            <Typography variant="body2" color="text.secondary">{formatDateBR(item.criadoEm)}</Typography>
                                        </Box>
                                    ))}
                                </Stack>
                            )}
                        </Paper>
                    </Stack>
                )}
            </DialogContent>
            <DialogActions sx={{px: 3, py: 2}}>
                <Button onClick={onClose}>Fechar</Button>
            </DialogActions>
        </Dialog>
    );
}

export default function FinanceiroReceivablesTab({
    items,
    pagination,
    loading,
    companies,
    boletos,
    statusFilter,
    empresaFilter,
    competenciaFilter,
    search,
    page,
    selectedReceivable,
    selectedReceivableHistory,
    onChangeStatusFilter,
    onChangeEmpresaFilter,
    onChangeCompetenciaFilter,
    onChangeSearch,
    onChangePage,
    onOpenDetails,
    onCloseDetails,
}) {
    return (
        <Stack spacing={3}>
            <Box sx={{p: 2.5, borderRadius: 2.5, bgcolor: '#f0fdfa', border: '1px solid #99f6e4'}}>
                <Stack direction="row" spacing={1.5} alignItems="flex-start">
                    <LinkIcon size={20} color="#0f766e" />
                    <Box>
                        <Typography sx={{color: '#134e4a', fontWeight: 700}}>Hub central de pagamentos</Typography>
                        <Typography variant="body2" sx={{color: '#0f766e'}}>
                            As contas a receber concentram faturas, boletos e demais meios de pagamento. A liquidação do boleto reflete automaticamente aqui.
                        </Typography>
                    </Box>
                </Stack>
            </Box>

            <Stack direction={{xs: 'column', md: 'row'}} spacing={1.5}>
                <TextField
                    value={search}
                    onChange={(event) => onChangeSearch?.(event.target.value)}
                    placeholder="Buscar por empresa, descrição ou documento..."
                    fullWidth
                    InputProps={{startAdornment: <Search size={18} color="#9ca3af" style={{marginRight: 8}} />}}
                />
                <TextField select value={empresaFilter || 'TODAS'} onChange={(event) => onChangeEmpresaFilter?.(event.target.value === 'TODAS' ? '' : event.target.value)} sx={{minWidth: 220}}>
                    <MenuItem value="TODAS">Todas as empresas</MenuItem>
                    {companies.map((item) => <MenuItem key={item.id} value={item.id}>{item.nome_fantasia || item.razao_social || item.nome}</MenuItem>)}
                </TextField>
                <TextField select value={statusFilter || 'TODOS'} onChange={(event) => onChangeStatusFilter?.(event.target.value === 'TODOS' ? '' : event.target.value)} sx={{minWidth: 200}}>
                    <MenuItem value="TODOS">Todos os status</MenuItem>
                    {['ABERTA', 'AGUARDANDO_PAGAMENTO', 'PAGA', 'PARCIALMENTE_PAGA', 'VENCIDA', 'CANCELADA'].map((option) => <MenuItem key={option} value={option}>{option}</MenuItem>)}
                </TextField>
                <TextField value={competenciaFilter || ''} onChange={(event) => onChangeCompetenciaFilter?.(event.target.value)} placeholder="Competência" sx={{minWidth: 160}} />
            </Stack>

            <Paper variant="outlined" sx={{borderRadius: 3, overflow: 'hidden', borderColor: '#e5e7eb'}}>
                <Table>
                    <TableHead>
                        <TableRow sx={{bgcolor: '#f9fafb'}}>
                            <TableCell>Empresa</TableCell>
                            <TableCell>Descrição</TableCell>
                            <TableCell>Documento</TableCell>
                            <TableCell>Valor</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Vencimento</TableCell>
                            <TableCell align="center">Boleto</TableCell>
                            <TableCell align="right">Ações</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {!items.length ? (
                            <TableRow>
                                <TableCell colSpan={8}>
                                    <Typography color="text.secondary" sx={{py: 3, textAlign: 'center'}}>
                                        {loading ? 'Carregando contas a receber...' : 'Nenhuma conta encontrada.'}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : items.map((item) => {
                            const company = companies.find((companyItem) => companyItem.id === item.empresaId);
                            const boleto = boletos.find((boletoItem) => boletoItem.contaReceberId === item.id);
                            const palette = getReceivableStatusPalette(item.status);
                            return (
                                <TableRow key={item.id} hover sx={item.status === 'VENCIDA' ? {bgcolor: '#fef2f2'} : undefined}>
                                    <TableCell>
                                        <Typography sx={{color: '#111827', fontWeight: 500}}>{company?.nome_fantasia || company?.razao_social || company?.nome || '-'}</Typography>
                                        <Typography variant="body2" color="text.secondary">{formatCpfCnpj(company?.cnpj || '') || '-'}</Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography sx={{color: '#111827'}}>{item.descricao}</Typography>
                                        <Typography variant="body2" color="text.secondary">{item.competencia || '-'}</Typography>
                                    </TableCell>
                                    <TableCell>{item.numeroDocumento || '-'}</TableCell>
                                    <TableCell>{formatMoney(item.valorTotal)}</TableCell>
                                    <TableCell>
                                        <Chip label={item.status} size="small" sx={{bgcolor: palette.bg, color: palette.color, border: `1px solid ${palette.border}`}} />
                                    </TableCell>
                                    <TableCell>{formatDateBR(item.dataVencimento)}</TableCell>
                                    <TableCell align="center">
                                        {boleto ? (
                                            <Chip label="Emitido" size="small" icon={<Receipt size={14} />} sx={{bgcolor: '#dcfce7', color: '#15803d', border: '1px solid #bbf7d0'}} />
                                        ) : (
                                            <Typography variant="body2" color="text.secondary">-</Typography>
                                        )}
                                    </TableCell>
                                    <TableCell align="right">
                                        <IconButton onClick={() => onOpenDetails?.(item.id)}><Eye size={18} /></IconButton>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </Paper>

            <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="body2" color="text.secondary">
                    Página {Number(page || 0) + 1} de {pagination?.total_pages || 1} • {pagination?.total || 0} contas
                </Typography>
                <Stack direction="row" spacing={1}>
                    <Button variant="outlined" disabled={!pagination?.has_previous} onClick={() => onChangePage?.(Math.max(0, Number(page || 0) - 1))}>Anterior</Button>
                    <Button variant="outlined" disabled={!pagination?.has_next} onClick={() => onChangePage?.(Number(page || 0) + 1)}>Próxima</Button>
                </Stack>
            </Stack>

            <ReceivableDetailsDialog
                open={Boolean(selectedReceivable)}
                receivable={selectedReceivable}
                history={selectedReceivableHistory}
                companies={companies}
                boletos={boletos}
                onClose={onCloseDetails}
            />
        </Stack>
    );
}
